/**
 * telemetry.ts — New Relic browser instrumentation for intake-web.
 *
 * Initializes the NR Browser Agent once at import time (side-effect).
 * Exports thin wrappers that callers use instead of touching window.newrelic
 * directly.
 *
 * No-ops when VITE_NR_BROWSER_KEY is absent — safe in dev/test.
 * Never throws — telemetry must not affect the applicant flow.
 *
 * Standard event fields (injected by logEvent):
 *   service.name = "civicforce"
 *   subsystem    = "intake-web"
 *   event_type   = <caller-supplied snake_case string>
 *
 * See docs/observability/logging.md for the full event catalog.
 * See docs/observability/nr-browser-agent-brief.md for implementation notes.
 *
 * PII NOTE: Phase 0 uses synthetic demo data only. Before Phase 1 (real CDLE
 * participants), review every logEvent() call for PII fields — omit or hash
 * session_id and any user-supplied strings. See ADR-011.
 */

import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

// ---------------------------------------------------------------------------
// Type augmentation — window.newrelic is set by the agent after init.
// Only the methods we actually use are declared here.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    newrelic?: {
      log(
        message: string,
        options?: {
          level?: 'trace' | 'debug' | 'info' | 'warn' | 'error';
          customAttributes?: Record<string, unknown>;
        },
      ): void;
      addPageAction(name: string, attributes?: Record<string, unknown>): void;
      noticeError(
        error: Error | string,
        customAttributes?: Record<string, string>,
      ): void;
    };
  }
}

// ---------------------------------------------------------------------------
// Agent initialization — runs once when this module is first imported.
// ---------------------------------------------------------------------------
const accountId = import.meta.env.VITE_NR_ACCOUNT_ID;
const appId     = import.meta.env.VITE_NR_APP_ID;
const browserKey = import.meta.env.VITE_NR_BROWSER_KEY;

if (accountId && appId && browserKey) {
  new BrowserAgent({
    init: {
      ajax: {
        // Prevent recursive telemetry calls to NR's own beacon endpoint.
        deny_list: ['bam.nr-data.net'],
      },
      browser_consent_mode: { enabled: false },
      distributed_tracing: { enabled: true },
      logging: { enabled: true },
      performance: {
        capture_detail: false,
        capture_marks: false,
        capture_measures: true,
      },
      privacy: { cookies_enabled: true },
      // session_replay MUST stay off — this is a government intake form.
      // Screen recording of applicant-supplied data is a PII violation.
      session_replay: { enabled: false },
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: browserKey,
      applicationID: appId,
      sa: 1,
    },
    loader_config: {
      accountID: accountId,
      trustKey: accountId,
      agentID: appId,
      licenseKey: browserKey,
      applicationID: appId,
    },
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Emit a structured log event. Lands in NR as event_type = eventType. */
export function logEvent(
  eventType: string,
  attrs: Record<string, string | number | boolean> = {},
): void {
  try {
    window.newrelic?.log(eventType, {
      level: 'info',
      customAttributes: {
        'service.name': 'civicforce',
        subsystem: 'intake-web',
        event_type: eventType,
        ...attrs,
      },
    });
  } catch {
    // swallowed
  }
}

/** Report a caught error to NR. Lands in NR as a JavaScriptError event. */
export function noticeError(
  error: Error | string,
  context?: Record<string, string>,
): void {
  try {
    window.newrelic?.noticeError(error, context);
  } catch {
    // swallowed
  }
}
